import {ColorTile, getHexFromColorCode} from '@smart/web-components';
import React from 'react';
import type {Maybe} from '../../graphql';
import {AppModelContext} from './app';
import styles from './colour-select.module.scss';
import Fields from './easy-form/fields';

interface ColourSelectProps {
  tag: string;
  required?: boolean;
  label?: Maybe<string>;
  initial?: string;
}

type BodyLacquer = 'metallic' | 'matte' | undefined;

interface ColorEntry {
  name: string;
  bodyLacquer?: BodyLacquer;
}

interface ColorData {
  colorMap: {
    exterior: {
      [key: string]: string;
    };
  };
  colors: {[key: string]: ColorEntry};
}

type CompiledColours = {
  value: string;
  children?: React.ReactNode;
}[];

const getHexFromColorCodeForHashtag5 = (code: string | undefined): string => {
  const hashtag5Colors: Record<string, string> = {
    pulsarrubymetallic: `#67091F`,
    terragreenmatte: `#54B235`,
    interstellarbluematte: `#8BA2DE`,
    saturnbeigematte: `#C7B59D`,
  };

  if (code) {
    const hastag5Color = hashtag5Colors[code];
    if (hastag5Color) {
      return hastag5Color;
    }
  }
  return getHexFromColorCode(code);
};

const colourMap = (data?: ColorData): CompiledColours => {
  if (!data) return [];
  const exterior = data.colorMap.exterior ?? {};
  const colors = data.colors ?? {};

  const colorLookup: {[key: string]: string[]} = {};
  for (const [code, colorInfo] of Object.entries(colors)) {
    const {name, bodyLacquer} = colorInfo;
    const key = `${name ?? ``}${bodyLacquer ?? ``}`;
    if (!colorLookup[key]) {
      colorLookup[key] = [];
    }
    colorLookup?.[key]?.push(code);
  }

  const compiledColours: CompiledColours = [];
  for (const [color, label] of Object.entries(exterior)) {
    const parts = color.split(`_`);
    const mainKey = parts[0];
    const secondaryKey = parts[1];
    if (mainKey && colorLookup?.[mainKey] && colorLookup?.[mainKey]?.[0]) {
      if (secondaryKey) {
        if (colorLookup?.[secondaryKey]?.[0]) {
          compiledColours.push({
            value: color,
            children: (
              <>
                <ColorTile
                  className={styles.colorTitle}
                  id={color}
                  primaryColor={getHexFromColorCodeForHashtag5(
                    colorLookup?.[mainKey]?.[0],
                  )}
                  secondaryColor={getHexFromColorCodeForHashtag5(
                    colorLookup?.[secondaryKey]?.[0],
                  )}
                  titleText={``}
                />
                &nbsp;
                {label}
              </>
            ),
          });
        }
      } else {
        compiledColours.push({
          value: color,
          children: (
            <>
              <ColorTile
                className={styles.colorTitle}
                id={color}
                primaryColor={getHexFromColorCodeForHashtag5(
                  colorLookup?.[mainKey]?.[0],
                )}
                secondaryColor={getHexFromColorCodeForHashtag5(
                  colorLookup?.[mainKey]?.[0],
                )}
                titleText={``}
              />
              &nbsp;
              {label}
            </>
          ),
        });
      }
    }
  }
  return compiledColours;
};

export const ColourSelect = React.memo(
  ({tag, required, label, initial}: ColourSelectProps): JSX.Element => {
    const {
      cms: {
        translations: defaultTranslations,
        formsFeatureAppNewsletterPopupUi,
      },
    } = React.useContext(AppModelContext);

    const colourOptions = colourMap(
      (formsFeatureAppNewsletterPopupUi?.colourTranslations?.translations ??
        defaultTranslations) as ColorData,
    );

    return (
      <Fields.Select
        style={{maxWidth: `370px`}}
        initial={initial}
        allowEmptySelection={!initial}
        label={label}
        tag={tag}
        lightTheme
        options={colourOptions}
        required={required}
      />
    );
  },
);
